/**
 * Sprint 24.5 — FirstPartyTemplateCard.
 *
 * One card in the Add-menu first-party section. State-driven (Q17): LIVE is a
 * clickable card with a solid "Connect →" CTA; COMING SOON is de-emphasised
 * (opacity 0.7), non-clickable, with a "Notify me" CTA; DEV ONLY gets a dashed
 * border and a ghost "Register" CTA. Brand (avatar bg/colour/icon) comes straight
 * from the template registry, so each card carries its connector's identity.
 */

import type { CSSProperties } from "react";
import type { FirstPartyTemplate } from "../../../../hooks/use-first-party-templates";
import { ConnectionTypeChip } from "../ConnectionTypeChip";
import { TemplateStatusBadge } from "./TemplateStatusBadge";
import { NotifyMeButton } from "./NotifyMeButton";

function footerIcon(t: FirstPartyTemplate): string {
  if (t.dev_only) return "ti-flask";
  if (t.status === "coming-soon") return "ti-clock";
  return "ti-lock";
}

export interface FirstPartyTemplateCardProps {
  template: FirstPartyTemplate;
  onSelect: (template: FirstPartyTemplate) => void;
}

export function FirstPartyTemplateCard({ template, onSelect }: FirstPartyTemplateCardProps) {
  const comingSoon = template.status === "coming-soon" || !template.enabled;
  const devOnly = template.dev_only;
  const chipType = template.type === "mcp" ? "mcp-first-party" : "wrapped-first-party";

  const container: CSSProperties = {
    background: "var(--color-background-primary)",
    border: devOnly
      ? "0.5px dashed var(--color-border-secondary)"
      : "0.5px solid var(--color-border-tertiary)",
    borderRadius: "var(--border-radius-md)",
    padding: "14px 16px",
    display: "flex",
    alignItems: "center",
    gap: 14,
    width: "100%",
    textAlign: "left",
    fontFamily: "var(--font-sans)",
    cursor: comingSoon ? "default" : "pointer",
    opacity: comingSoon ? 0.7 : 1,
  };

  const avatar: CSSProperties = {
    width: 44,
    height: 44,
    borderRadius: "var(--border-radius-md)",
    background: template.brand.avatar_bg,
    color: template.brand.avatar_color,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    flexShrink: 0,
  };

  const inner = (
    <>
      <span style={avatar}>
        <i className={`ti ti-${template.brand.icon_name}`} aria-hidden="true" />
      </span>
      <span style={bodyCol}>
        <span style={titleRow}>
          <span style={name}>{template.display_name}</span>
          <TemplateStatusBadge status={template.status} />
          <ConnectionTypeChip type={chipType} size="small" />
        </span>
        <span style={description}>{template.description}</span>
        <span style={footer}>
          <i className={`ti ${footerIcon(template)}`} style={footerIconStyle} aria-hidden="true" />
          {template.capability_footer}
        </span>
      </span>
      <span style={ctaWrap}>
        {comingSoon ? (
          <NotifyMeButton templateSlug={template.slug} templateName={template.display_name} />
        ) : devOnly ? (
          <span style={ghostCta}>Register</span>
        ) : (
          <span style={primaryCta}>Connect →</span>
        )}
      </span>
    </>
  );

  // COMING SOON cards are non-clickable containers (the Notify-me CTA owns the
  // interaction); LIVE / DEV ONLY are clickable buttons that open the wizard.
  if (comingSoon) {
    return <div style={container}>{inner}</div>;
  }
  return (
    <button type="button" style={container} onClick={() => onSelect(template)}>
      {inner}
    </button>
  );
}

const bodyCol: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const titleRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  minWidth: 0,
  flexWrap: "wrap",
};

const name: CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: "var(--color-text-primary)",
};

const description: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  lineHeight: 1.5,
};

const footer: CSSProperties = {
  fontSize: 10,
  color: "var(--color-text-tertiary)",
  marginTop: 2,
  display: "flex",
  alignItems: "center",
  gap: 4,
};

const footerIconStyle: CSSProperties = { fontSize: 11, flexShrink: 0 };

const ctaWrap: CSSProperties = { flexShrink: 0, display: "flex", alignItems: "center" };

const primaryCta: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  padding: "7px 14px",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  whiteSpace: "nowrap",
};

const ghostCta: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  padding: "7px 14px",
  borderRadius: "var(--border-radius-sm)",
  background: "transparent",
  color: "var(--color-text-secondary)",
  border: "0.5px dashed var(--color-border-secondary)",
  whiteSpace: "nowrap",
};
